import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import { parseCsv, toCsv, downloadCsv } from "../../../utils/csv";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface Consultant {
  id: number;
  name: string;
  mobile?: string;
  specialization?: string;
  is_active?: boolean;
}

interface FormState {
  name: string;
  mobile: string;
  specialization: string;
}

export default function Consultants() {
  const { showToast } = useToast();
  const specializationOptions = [
    "General Dentistry",
    "Endodontics",
    "Prosthodontics",
    "Orthodontics",
    "Oral Surgery",
    "Periodontics",
    "Pedodontics",
    "Implantology",
    "Oral Medicine",
    "Oral Pathology",
    "Radiology",
    "Restorative",
    "Cosmetic Dentistry",
    "TMJ",
    "Public Health",
  ];
  const [rows, setRows] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const importRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({ name: "", mobile: "", specialization: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/admin/consultants") as Consultant[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast({ message: "Failed to load consultants", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = () => {
    if (!rows.length) {
      showToast({ message: "No data to export.", type: "warning" });
      return;
    }
    const headers = ["name", "mobile", "specialization", "is_active"];
    const data = rows.map((c) => ({
      name: c.name,
      mobile: c.mobile || "",
      specialization: c.specialization || "",
      is_active: c.is_active ?? true,
    }));
    const csv = toCsv(headers, data);
    downloadCsv(csv, "consultants.csv");
    showToast({ message: "Export downloaded.", type: "info" });
  };

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    const csvRows = parseCsv(text);
    if (csvRows.length < 2) return showToast({ message: "CSV is empty", type: "error" });
    const headers = csvRows[0].map((h) => h.trim().toLowerCase());
    const dataRows = csvRows.slice(1);

    showToast({ message: "Importing CSV data...", type: "info" });
    let errorCount = 0;

    for (const row of dataRows) {
      const get = (key: string) => {
        const idx = headers.indexOf(key);
        return idx >= 0 ? (row[idx] ?? "").trim() : "";
      };
      const name = get("name");
      if (!name) continue;
      const payload = {
        name,
        mobile: get("mobile") || undefined,
        specialization: get("specialization") || undefined,
      };

      try {
        const existing = rows.find((c) => c.name === name);
        if (existing) {
          await apiPut(`/api/admin/consultants/${existing.id}`, payload);
        } else {
          await apiPost("/api/admin/consultants", payload);
        }
      } catch (e) {
        errorCount++;
        console.error("Row import failed", row, e);
      }
    }
    
    if (errorCount > 0) {
      showToast({ message: `Import finished with ${errorCount} errors.`, type: "warning" });
    } else {
      showToast({ message: "Import successful.", type: "success" });
    }
    load();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return showToast({ message: "Consultant name required", type: "warning" });

    let finalName = form.name.trim();
    if (!/^Dr\.?\s/i.test(finalName)) {
      finalName = `Dr. ${finalName}`;
    }

    const payload = {
      name: finalName,
      mobile: form.mobile,
      specialization: form.specialization,
    };

    try {
      if (editingId) {
        await apiPut(`/api/admin/consultants/${editingId}`, payload);
        showToast({ message: "Consultant Updated", type: "success" });
      } else {
        await apiPost("/api/admin/consultants", payload);
        showToast({ message: "Consultant Added", type: "success" });
      }

      setForm({ name: "", mobile: "", specialization: "" });
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ message: "Operation failed", type: "error" });
    }
  };

  const handleEdit = (row: Consultant) => {
    setForm({
      name: row.name,
      mobile: row.mobile || "",
      specialization: row.specialization || "",
    });
    setEditingId(row.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setForm({ name: "", mobile: "", specialization: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleStatus = async (row: Consultant) => {
    try {
      await apiPut(`/api/admin/consultants/${row.id}`, { is_active: !row.is_active });
      load();
    } catch (e) {
      showToast({ message: "Failed to toggle status", type: "error" });
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this consultant?")) return;
    try {
      await apiDelete(`/api/admin/consultants/${id}`);
      showToast({ message: "Consultant deleted", type: "success" });
      load();
    } catch (e) {
      showToast({ message: "Failed to delete consultant", type: "error" });
    }
  };

  const filtered = rows.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.specialization && c.specialization.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', gap: '16px', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search consultant..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', minWidth: '250px' }}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              if (e.target) e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => importRef.current?.click()}>Import CSV</Button>
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button variant="primary" onClick={() => { setShowForm(!showForm); if (!showForm) { setForm({ name: "", mobile: "", specialization: "" }); setEditingId(null); } }}>
            {showForm ? "Cancel Form" : "+ Add Consultant"}
          </Button>
        </div>
      </div>

      {showForm && (
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr auto", gap: "12px", padding: '16px', background: editingId ? '#eff6ff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <input
            placeholder="Name (e.g. Dr. Smith)"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />
          <input
            placeholder="Mobile"
            value={form.mobile}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, mobile: e.target.value })}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />
          <select
            value={form.specialization}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setForm({ ...form, specialization: e.target.value })}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          >
            <option value="">Select specialization</option>
            {specializationOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="primary" onClick={handleSubmit}>
              {editingId ? "💾 Update" : "➕ Add"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div style={{ overflowX: 'auto', padding: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
          <thead style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
            <tr>
              <th style={{ padding: '12px 8px' }}>#</th>
              <th style={{ padding: '12px 8px' }}>Name</th>
              <th style={{ padding: '12px 8px' }}>Mobile</th>
              <th style={{ padding: '12px 8px' }}>Specialization</th>
              <th style={{ padding: '12px 8px' }}>Status</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>Loading consultants...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No consultants found.</td></tr>
            ) : (
              filtered.map((c, idx) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', background: editingId === c.id ? '#f0f9ff' : 'transparent' }}>
                  <td style={{ padding: '12px 8px', color: '#64748b' }}>{idx + 1}</td>
                  <td style={{ padding: '12px 8px', fontWeight: 600, color: '#0f172a' }}>👨‍⚕️ {c.name}</td>
                  <td style={{ padding: '12px 8px' }}>{c.mobile || "—"}</td>
                  <td style={{ padding: '12px 8px' }}>
                    {c.specialization ? <span style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px' }}>{c.specialization}</span> : "—"}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <span 
                      style={{ color: c.is_active ? '#16a34a' : '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => toggleStatus(c)}
                    >
                      {c.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(c)}>Edit</Button>
                      <Button variant="danger" size="sm" onClick={() => remove(c.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '16px', color: '#64748b', fontSize: '0.85rem' }}>
        * Consultants added here are used by Doctor (referrals) &amp; Manager (billing) modules.
        Granular per-procedure fees are managed in the Manager module under Consultants.
      </div>
    </div>
  );
}
